import type { MigrationDeployment as SchemaDeployment } from '@schema';
import {
  DEPLOYMENT_STATUS,
  DEPLOYMENT_STATUS_TEXT,
  DEPLOYMENT_STATUS_ICON,
  DEPLOYMENT_STATUS_NORMALIZE,
  type DeploymentStatus
} from '@/common/constants/deployment.constants';
import { dateFormatter } from '@/common/utils/formatter.date';
import {
  type SchemaBaseTask,
  type MappedProgressUpdate,
  formatTaskType,
  taskStatusIcon,
  mapProgressUpdates
} from '@/common/utils/taskMapper';

// These interfaces extend the base API schema with fields present in the actual
// API response. Replace with @schema types once the backend schema package is updated.
interface SchemaDeploymentTask extends SchemaBaseTask {
  execution_id: string;
  process_id: number | null;
  events: unknown[];
}

type SchemaDeploymentExt = SchemaDeployment & {
  instances?: Record<string, { name?: string }>;
  tasks?: SchemaDeploymentTask[];
  last_execution_status?: string;
  deployment_opts?: {
    clone_disks?: boolean;
    force?: boolean;
    os_morphing?: boolean;
    save_snapshot?: boolean;
    start?: boolean;
  };
};

export type { MappedProgressUpdate as DeploymentProgressUpdate };

export interface MappedDeploymentTask {
  id: string;
  taskType: string;
  taskTypeText: string;
  status: string;
  statusText: string;
  statusIcon: string;
  instance: string;
  host: string | null;
  onError: boolean;
  dependsOn: string[];
  exceptionDetails: string | null;
  progressUpdates: MappedProgressUpdate[];
  startTime: string;
  updatedAt: string;
}

export interface MappedDeployment {
  id: string;
  migrationId: string;
  name: string;
  date: string;
  status: DeploymentStatus;
  dateFormatted: string;
  statusText: string;
  statusIcon: string;
  tasks: MappedDeploymentTask[];
  deploymentOpts: {
    cloneDisks: boolean;
    force: boolean;
    osMorphing: boolean;
    saveSnapshot: boolean;
    start: boolean;
  };
}

export const mapDeploymentTask = (task: SchemaDeploymentTask): MappedDeploymentTask => ({
  id: task.id,
  taskType: task.task_type,
  taskTypeText: formatTaskType(task.task_type),
  status: task.status,
  statusText: formatTaskType(task.status),
  statusIcon: taskStatusIcon(task.status),
  instance: task.instance,
  host: task.host,
  onError: task.on_error,
  dependsOn: task.depends_on ?? [],
  exceptionDetails: task.exception_details,
  progressUpdates: mapProgressUpdates(task.progress_updates),
  startTime: task.created_at,
  updatedAt: task.updated_at ?? ''
});

const mapper = (d: SchemaDeploymentExt, migrationId: string): MappedDeployment => {
  const status = DEPLOYMENT_STATUS_NORMALIZE[d.last_execution_status ?? d.status ?? ''] ?? DEPLOYMENT_STATUS.DEPLOYING;
  const tasks = (d.tasks ?? [])
    .slice()
    .sort((a, b) => a.index - b.index)
    .map(mapDeploymentTask);
  const date = tasks[0]?.startTime ?? new Date().toISOString();
  return {
    id: d.id ?? '',
    migrationId,
    name: d.name
      ?? (Object.values(d.instances ?? {}).map(i => i.name).filter(Boolean).join(', ')
        || Object.keys(d.instances ?? {}).join(', ')),
    date,
    status,
    dateFormatted: dateFormatter(date),
    statusText: DEPLOYMENT_STATUS_TEXT[status],
    statusIcon: DEPLOYMENT_STATUS_ICON[status],
    tasks,
    deploymentOpts: {
      cloneDisks: d.deployment_opts?.clone_disks ?? false,
      force: d.deployment_opts?.force ?? false,
      osMorphing: d.deployment_opts?.os_morphing ?? false,
      saveSnapshot: d.deployment_opts?.save_snapshot ?? false,
      start: d.deployment_opts?.start ?? false
    }
  };
};

export default mapper;
